import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaCheck, FaGlobeAmericas, FaIndustry, FaLayerGroup, FaMicrochip, FaShippingFast, FaTools } from "react-icons/fa";

export const icons = {
  industry: FaIndustry,
  tools: FaTools,
  global: FaGlobeAmericas,
  shipping: FaShippingFast,
  layers: FaLayerGroup,
  chip: FaMicrochip,
};

export function Eyebrow({ children, light = false }) {
  return <p className={`text-xs font-semibold uppercase tracking-[0.28em] ${light ? "text-[#d6ad73]" : "text-[#9a6a35]"}`}>{children}</p>;
}

export function Hero({ eyebrow, title, text, image, actions = [], stats = [] }) {
  return (
    <section className="relative isolate overflow-hidden bg-[#07111f] text-white">
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/95 via-[#07111f]/72 to-[#07111f]/24" />
      </div>
      <div className="relative mx-auto grid min-h-[620px] max-w-7xl content-end px-6 pb-14 pt-24 md:min-h-[680px] md:pt-28">
        <div className="max-w-4xl animate-rise text-shadow-hero">
          <Eyebrow light>{eyebrow}</Eyebrow>
          <h1 className="mt-5 max-w-full text-4xl font-semibold leading-tight tracking-normal sm:text-5xl md:max-w-5xl md:text-7xl md:leading-[1.02]">{title}</h1>
          <p className="mt-6 max-w-3xl text-base leading-8 text-slate-200 md:text-xl">{text}</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            {actions.map((action, index) => (
              <Link key={action.to} to={action.to} className={`inline-flex w-full items-center justify-center gap-2 rounded px-6 py-3 text-sm font-semibold transition sm:w-auto ${index === 0 ? "bg-[#b8894d] text-white hover:bg-[#a47942]" : "border border-white/25 bg-white/5 text-white hover:bg-white/10"}`}>
                {action.label}<FaArrowRight className="text-xs" />
              </Link>
            ))}
          </div>
        </div>
        {stats.length > 0 && (
          <div className="mt-16 grid gap-px overflow-hidden rounded border border-white/10 bg-white/10 md:grid-cols-3">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-[#07111f]/75 p-6 backdrop-blur">
                <div className="text-3xl font-semibold text-white">{stat.value}</div>
                <div className="mt-2 text-sm text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function SectionHeader({ eyebrow, title, text, centered = false }) {
  return (
    <div className={centered ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#101820] md:text-5xl">{title}</h2>
      {text && <p className="mt-5 text-base leading-8 text-slate-600 md:text-lg">{text}</p>}
    </div>
  );
}

export function CardGrid({ items, columns = "lg:grid-cols-3" }) {
  return (
    <div className={`grid gap-5 md:grid-cols-2 ${columns}`}>
      {items.map((item) => {
        const Icon = item.icon || FaTools;
        return (
          <article key={item.title} className="group rounded border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#c9a36a] hover:shadow-xl">
            <div className="mb-7 flex h-12 w-12 items-center justify-center rounded bg-[#f4efe7] text-[#9a6a35]"><Icon /></div>
            <h3 className="text-xl font-semibold text-[#101820]">{item.title}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-600">{item.text}</p>
            {item.points && (
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {item.points.map((point) => <li key={point} className="flex gap-3"><FaCheck className="mt-1 text-xs text-[#b8894d]" /><span>{point}</span></li>)}
              </ul>
            )}
          </article>
        );
      })}
    </div>
  );
}

export function SplitFeature({ eyebrow, title, text, image, points = [], reverse = false }) {
  return (
    <section className="bg-white px-6 py-24">
      <div className={`mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <div>
          <SectionHeader eyebrow={eyebrow} title={title} text={text} />
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {points.map((point) => <div key={point} className="flex items-center gap-3 rounded border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700"><FaCheck className="text-[#b8894d]" />{point}</div>)}
          </div>
        </div>
        <div className="overflow-hidden rounded border border-slate-200 shadow-2xl">
          <img src={image} alt="" className="h-[460px] w-full object-cover transition duration-700 hover:scale-105" />
        </div>
      </div>
    </section>
  );
}

export function CTA() {
  return (
    <section className="bg-[#101820] px-6 py-20 text-white">
      <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 lg:flex-row lg:items-center">
        <div>
          <Eyebrow light>Start a technical discussion</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold md:text-5xl">Bring us the specification, drawing, sample, or sourcing brief.</h2>
          <p className="mt-5 max-w-3xl leading-8 text-slate-300">DS Global Machines coordinates supplier qualification, engineering review, quotation, production follow-up, inspection, documentation, and export logistics.</p>
        </div>
        <Link to="/contact" className="inline-flex shrink-0 items-center justify-center gap-2 rounded bg-[#b8894d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a47942]">Request a Quote<FaArrowRight className="text-xs" /></Link>
      </div>
    </section>
  );
}


